import * as React from 'react'
import Link from 'next/link'
import Image from 'next/image'

import { styled } from '@mui/material/styles'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import { Box, BoxProps, Breadcrumbs, Link as MuiLink } from '@mui/material'
import Stack from '@mui/material/Stack'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import { HeaderBarHeightPixel } from 'components/atomic/organisms/header'
import ResponsiveContainer from 'components/atomic/organisms/responsive-container'
import BackgroundImage from 'components/atomic/atoms/bg-image'
import Text from 'components/atomic/atoms/text'
import Chip from 'components/atomic/atoms/chip'

import {
    TwitterShareButton,
    TwitterIcon,
    TelegramShareButton,
    TelegramIcon,
    WhatsappShareButton,
    WhatsappIcon,
    LinkedinShareButton,
    LinkedinIcon,
    EmailShareButton,
    EmailIcon,
} from 'react-share'

const HeroWrapper = styled(Box)`
    position: relative;
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};
    margin-top: ${`${HeaderBarHeightPixel}px`};
`

interface HeroProps extends BoxProps {
    title: string
    description: string
    logo?: string
    categoriesTitles: string[]
    breadcrumbs: {
        title: string
        href?: string
    }[]
    color?: string
}

const Hero = (props: HeroProps) => {
    const { t }: I18n = useTranslation()

    return (
        <HeroWrapper sx={props.sx}>
            <BackgroundImage filterColor={props.color} imageUrl="/assets/pattern-bg1.png" />

            <ResponsiveContainer maxWidth="lg" sx={{ paddingY: 4 }}>
                <Stack direction={'row'}>
                    <Stack width={{ md: '60%' }}>
                        {/* Breadcrumbs */}
                        <Breadcrumbs aria-label="breadcrumb">
                            {props.breadcrumbs.map((breadcrumb, index) =>
                                breadcrumb.href === undefined ? (
                                    <Text
                                        key={index}
                                        variant="body2"
                                        sx={{ display: 'flex', alignItems: 'center' }}
                                        color="inherit">
                                        {index === 0 && <HomeRoundedIcon sx={{ mr: 0.5 }} fontSize="inherit" />}
                                        {breadcrumb.title}
                                    </Text>
                                ) : (
                                    <Link key={index} href={breadcrumb.href} passHref>
                                        <MuiLink
                                            underline="hover"
                                            sx={{ display: 'flex', alignItems: 'center' }}
                                            color="inherit"
                                            variant="body2">
                                            {index === 0 && <HomeRoundedIcon sx={{ mr: 0.5 }} fontSize="inherit" />}
                                            {breadcrumb.title}
                                        </MuiLink>
                                    </Link>
                                ),
                            )}
                        </Breadcrumbs>

                        {/* Share icons */}
                        <Stack direction="row" spacing={1} sx={{ mt: 5 }}>
                            <TwitterShareButton
                                url={'shareUrl'}
                                title={'title'}
                                className="Demo__some-network__share-button">
                                <TwitterIcon size={24} round />
                            </TwitterShareButton>
                            <TelegramShareButton
                                url={'shareUrl'}
                                title={'title'}
                                className="Demo__some-network__share-button">
                                <TelegramIcon size={24} round />
                            </TelegramShareButton>
                            <WhatsappShareButton
                                url={'shareUrl'}
                                title={'title'}
                                separator=":: "
                                className="Demo__some-network__share-button">
                                <WhatsappIcon size={24} round />
                            </WhatsappShareButton>
                            <LinkedinShareButton url={'shareUrl'} className="Demo__some-network__share-button">
                                <LinkedinIcon size={24} round />
                            </LinkedinShareButton>
                            <EmailShareButton
                                url={'shareUrl'}
                                subject={'title'}
                                body="body"
                                className="Demo__some-network__share-button">
                                <EmailIcon size={24} round />
                            </EmailShareButton>
                        </Stack>

                        {/** Title & description */}
                        <Text variant="h2">{props.title}</Text>
                        <Text color="text.secondary">{props.description}</Text>

                        {/** Categories */}
                        <Box display="flex" flexWrap="wrap" gap={1} mt={4} maxHeight={'50vh'} overflow={'scroll'}>
                            {props.categoriesTitles.slice(0, 5).map((categoryTitle, index) => (
                                <Chip key={index} size="small" label={categoryTitle} />
                            ))}

                            {props.categoriesTitles.length > 5 ? (
                                <Chip size="small" label={`+ ${props.categoriesTitles.length - 4} altri`} />
                            ) : null}
                        </Box>
                    </Stack>

                    {/* Logo */}
                    {props.logo ? (
                        <Stack
                            justifyContent="center"
                            height={'inherit'}
                            flexGrow={1}
                            sx={{ display: { xs: 'none', md: 'flex' } }}>
                            <Image
                                alt={`${props.title} logo`}
                                height={200}
                                width={200}
                                objectFit="contain"
                                style={{ opacity: 0.7, flex: 'none' }}
                                src={props.logo}
                                unoptimized
                            />
                        </Stack>
                    ) : null}
                </Stack>
            </ResponsiveContainer>
        </HeroWrapper>
    )
}

export default Hero
